import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ApiError, useEnrich, useSite } from "../../lib/api";
import { label, SITE_TYPE_LABEL } from "../../lib/tiers";
import { TierBadge } from "../Badge";
import type { PinState } from "../MapCanvas";
import { useToast } from "../Toast";
import { ActivityTab } from "./ActivityTab";
import { CoolingTab } from "./CoolingTab";
import { EvidenceTab } from "./EvidenceTab";
import { OverviewTab } from "./OverviewTab";
import { ContactsTab } from "./PlaceholderTabs";
import { UnitsTab } from "./UnitsTab";

export type VerifyController = {
  pin: PinState;
  saving: boolean;
  start: (siteId: number, lat: number, lon: number) => void;
  save: () => void;
  cancel: () => void;
};

export const TABS = [
  { id: "overview", label: "Overview" },
  { id: "units", label: "Units" },
  { id: "cooling", label: "Cooling" },
  { id: "contacts", label: "Contacts" },
  { id: "evidence", label: "Evidence" },
  { id: "activity", label: "Activity" },
] as const;
export type TabId = (typeof TABS)[number]["id"];

type Props = {
  siteId: number;
  tab: TabId;
  onTab: (t: TabId) => void;
  onClose?: () => void;
  verify: VerifyController | null;
  variant: "drawer" | "page";
  onAddToList?: (siteId: number) => void;
  headerExtra?: ReactNode;
};

export function SitePanel({
  siteId,
  tab,
  onTab,
  onClose,
  verify,
  variant,
  onAddToList,
  headerExtra,
}: Props) {
  const q = useSite(siteId);
  const enrich = useEnrich();
  const toast = useToast();
  const startEnrich = async () => {
    try {
      const r = await enrich.mutateAsync({ site_ids: [siteId], allow_paid_apis: false });
      if (r.status === "queued")
        toast(`Enrichment started: ${r.workflows.map((w) => w.workflow_id).join(", ")}`);
      else toast(`Needs approval (#${r.approval_id}): ${r.reason}`, "muted");
    } catch (e) {
      toast(
        e instanceof ApiError
          ? e.status === 403
            ? "Admin role required to enrich"
            : e.message
          : "Enrich failed",
        "danger",
      );
    }
  };
  if (q.isLoading) {
    return (
      <div className="p-4" style={{ color: "var(--muted)" }} data-testid="site-panel-loading">
        Loading site {siteId}…
      </div>
    );
  }
  if (q.error || !q.data) {
    const msg = q.error instanceof ApiError ? q.error.message : "Could not load site";
    return (
      <div className="p-4">
        <p style={{ color: "var(--danger)" }}>{msg}</p>
        {onClose ? (
          <button type="button" className="btn btn-sm mt-2" onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
    );
  }
  const d = q.data;
  const s = d.site;
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="site-panel">
      <header className="shrink-0 border-b px-4 pt-3 pb-2" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2
              className="truncate text-[16px] font-semibold leading-tight"
              data-testid="site-name"
            >
              {s.name}
            </h2>
            <div
              className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[12px]"
              style={{ color: "var(--muted)" }}
            >
              <span>
                {s.city ? `${s.city}, ` : ""}
                {s.state}
              </span>
              <span>·</span>
              <span>{s.operator?.name ?? "operator unknown"}</span>
              <span>·</span>
              <span>{label(SITE_TYPE_LABEL, s.site_type)}</span>
              <TierBadge tier={d.score?.tier ?? "x"} score={d.score?.score ?? null} />
            </div>
          </div>
          {onAddToList ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onAddToList(s.id)}
              data-testid="drawer-add-to-list"
            >
              + List
            </button>
          ) : null}
          {variant === "drawer" ? (
            <Link
              to="/sites/$id"
              params={{ id: String(s.id) }}
              className="btn btn-ghost btn-sm"
              aria-label="Open full page"
            >
              ⤢
            </Link>
          ) : null}
          {headerExtra}
          {onClose ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              aria-label="Close drawer"
              data-testid="drawer-close"
            >
              ✕
            </button>
          ) : null}
        </div>
        <div className="tabs mt-2 -mb-2 overflow-x-auto" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              className="tab"
              aria-selected={tab === t.id}
              onClick={() => onTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
              {t.id === "units" ? (
                <span className="num ml-1" style={{ color: "var(--muted-2)" }}>
                  {d.units.filter((u) => u.in_band).length}
                </span>
              ) : null}
              {t.id === "evidence" ? (
                <span className="num ml-1" style={{ color: "var(--muted-2)" }}>
                  {d.claims.length}
                </span>
              ) : null}
              {t.id === "contacts" ? (
                <span className="num ml-1" style={{ color: "var(--muted-2)" }}>
                  {d.contacts.length}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </header>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 py-3" role="tabpanel">
        {tab === "overview" ? <OverviewTab detail={d} verify={verify} /> : null}
        {tab === "units" ? <UnitsTab detail={d} wide={variant === "page"} /> : null}
        {tab === "cooling" ? <CoolingTab detail={d} /> : null}
        {tab === "contacts" ? <ContactsTab detail={d} /> : null}
        {tab === "evidence" ? <EvidenceTab detail={d} /> : null}
        {tab === "activity" ? <ActivityTab siteId={d.site.id} /> : null}
      </div>
    </div>
  );
}
